import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import extraStyles from '../../tools/components/AgentExtras.module.css';
import pluginStyles from '../../ai-tool-plugins/components/ToolPluginsPage.module.css';
import {
  AlertIcon,
  CheckIcon,
  CloseIcon,
  LayersIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '../../ai-tool-plugins/components/ToolPluginIcons';
import { listAiSkills } from '../../ai-skills/services/skillService';
import type { AiSkillDefinition } from '../../ai-skills/types/Skill';
import { createAiCommand, deleteAiCommand, listAiCommands, updateAiCommand } from '../services/commandService';
import type { AiCommandDefinition } from '../types/Command';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';

type DrawerMode = 'create' | 'edit' | null;
type StatusFilter = 'all' | 'published' | 'draft';
type FormState = {
  commandId: string;
  trigger: string;
  displayName: string;
  description: string;
  skillId: string;
  extraInstruction: string;
  isPublished: boolean;
  assignedUseCaseIds: AiPromptUseCaseId[];
};

const emptyForm = (): FormState => ({
  commandId: '',
  trigger: '',
  displayName: '',
  description: '',
  skillId: '',
  extraInstruction: '',
  isPublished: false,
  assignedUseCaseIds: [...DEFAULT_ASSIGNED_USE_CASE_IDS],
});

const TRIGGER_PATTERN = /^[a-z][a-z0-9-]{0,62}$/i;

export const CommandsPage: React.FC = () => {
  const { t } = useTranslation('agentCommands');
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission([PermissionCodes.aiConfigManage]);
  const [items, setItems] = useState<AiCommandDefinition[]>([]);
  const [skills, setSkills] = useState<AiSkillDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formIssue, setFormIssue] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [commandRows, skillRows] = await Promise.all([listAiCommands(), listAiSkills().catch(() => [])]);
      setItems(commandRows);
      setSkills(skillRows);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const visibleItems = useMemo(() => {
    const search = query.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter === 'published' && !item.isPublished) return false;
      if (statusFilter === 'draft' && item.isPublished) return false;
      if (!search) return true;
      return item.trigger.toLowerCase().includes(search)
        || item.displayName.toLowerCase().includes(search)
        || item.description.toLowerCase().includes(search);
    });
  }, [items, query, statusFilter]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormIssue(null);
    setDrawerMode('create');
  };

  const openEdit = (item: AiCommandDefinition) => {
    setForm({
      commandId: item.commandId,
      trigger: item.trigger,
      displayName: item.displayName,
      description: item.description,
      skillId: item.skillId ?? '',
      extraInstruction: item.extraInstruction,
      isPublished: item.isPublished,
      assignedUseCaseIds: [...item.assignedUseCaseIds],
    });
    setFormIssue(null);
    setDrawerMode('edit');
  };

  const validate = (): string | null => {
    if (!form.trigger.trim()) return 'triggerRequired';
    if (!TRIGGER_PATTERN.test(form.trigger.trim().replace(/^\//, ''))) return 'triggerInvalid';
    if (!form.displayName.trim()) return 'displayNameRequired';
    if (!form.skillId.trim() && !form.extraInstruction.trim()) return 'actionRequired';
    return null;
  };

  const handleSave = async () => {
    if (!canManage) return;
    const issue = validate();
    if (issue) {
      setFormIssue(issue);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        trigger: form.trigger.trim().replace(/^\//, ''),
        displayName: form.displayName.trim(),
        description: form.description.trim(),
        skillId: form.skillId.trim() || null,
        extraInstruction: form.extraInstruction.trim(),
        isPublished: form.isPublished,
        assignedUseCaseIds: form.assignedUseCaseIds,
      };
      if (drawerMode === 'edit') {
        await updateAiCommand(form.commandId, payload);
        showToast(t('updated'), 'success');
      } else {
        await createAiCommand({ ...payload, commandId: form.commandId.trim() || undefined });
        showToast(t('created'), 'success');
      }
      setDrawerMode(null);
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: AiCommandDefinition) => {
    if (!canManage) return;
    if (!window.confirm(t('confirmDelete', { name: item.displayName }))) return;
    try {
      await deleteAiCommand(item.commandId);
      showToast(t('deleted'), 'success');
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDelete'), 'error');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className={`${pluginStyles.page} ${extraStyles.page}`}>
      <div className={pluginStyles.pageBar}>
        <div className={pluginStyles.statStrip}>
          <span className={pluginStyles.stat}>
            <span className={pluginStyles.statValue}>{items.length}</span>
            {t('statItems')}
          </span>
          <span className={pluginStyles.stat}>
            <span className={pluginStyles.statValue}>{items.filter((item) => item.isPublished).length}</span>
            {t('statPublished')}
          </span>
        </div>
        {canManage ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <PlusIcon />
            <span>{t('create')}</span>
          </button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <div className={pluginStyles.empty}>
          <span className={pluginStyles.emptyIcon}><LayersIcon size={22} /></span>
          <span className={pluginStyles.emptyTitle}>{t('emptyTitle')}</span>
          <p className={pluginStyles.emptyText}>{t('emptyText')}</p>
          {canManage ? (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <PlusIcon />
              <span>{t('create')}</span>
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className={pluginStyles.listToolbar}>
            <div className={`${pluginStyles.searchField} ${pluginStyles.listToolbarSearch}`}>
              <SearchIcon className={pluginStyles.searchFieldIcon} size={15} />
              <input
                className={pluginStyles.searchInput}
                value={query}
                placeholder={t('search')}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className={pluginStyles.segmented}>
              {(['all', 'published', 'draft'] as StatusFilter[]).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`${pluginStyles.segment} ${statusFilter === id ? pluginStyles.segmentActive : ''}`}
                  onClick={() => setStatusFilter(id)}
                >
                  {t(id === 'all' ? 'filterAll' : id === 'published' ? 'filterPublished' : 'filterDraft')}
                </button>
              ))}
            </div>
          </div>
          {visibleItems.length === 0 ? (
            <p className={pluginStyles.emptyText}>{t('noSearchResults')}</p>
          ) : (
            <div className={pluginStyles.pluginGrid}>
              {visibleItems.map((item) => (
                <article
                  key={item.commandId}
                  className={`${pluginStyles.pluginCard} ${item.isPublished ? pluginStyles.pluginCardPublished : ''}`}
                >
                  <div className={pluginStyles.pluginCardTop}>
                    <span className={pluginStyles.pluginName}>{item.displayName}</span>
                    <span className={`${pluginStyles.badge} ${item.isPublished ? pluginStyles.badgeSuccess : pluginStyles.badgeWarn}`}>
                      {item.isPublished ? <CheckIcon size={12} /> : null}
                      {item.isPublished ? t('published') : t('draft')}
                    </span>
                  </div>
                  <div className={pluginStyles.pluginBadges}>
                    <span className={`${pluginStyles.badge} ${pluginStyles.badgeMono}`}>/{item.trigger}</span>
                  </div>
                  {item.description ? <p className={pluginStyles.pluginText}>{item.description}</p> : null}
                  <div className={pluginStyles.pluginCardActions}>
                    <button type="button" className={pluginStyles.iconButton} onClick={() => openEdit(item)}>
                      <PencilIcon size={15} />
                    </button>
                    {canManage ? (
                      <button
                        type="button"
                        className={`${pluginStyles.iconButton} ${pluginStyles.iconButtonDanger}`}
                        onClick={() => void handleDelete(item)}
                      >
                        <TrashIcon size={15} />
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {drawerMode ? (
        <div className={pluginStyles.drawerOverlay} onClick={() => !saving && setDrawerMode(null)}>
          <div className={pluginStyles.drawer} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className={pluginStyles.drawerHead}>
              <div>
                <h3 className={pluginStyles.drawerTitle}>{drawerMode === 'edit' ? t('edit') : t('create')}</h3>
                <p className={pluginStyles.drawerSubtitle}>{t('formHelp')}</p>
              </div>
              <button type="button" className={pluginStyles.iconButton} onClick={() => setDrawerMode(null)}>
                <CloseIcon size={15} />
              </button>
            </div>
            <div className={pluginStyles.drawerBody}>
              {formIssue ? (
                <div className={pluginStyles.formError}>
                  <AlertIcon size={15} />
                  <span>{t(`issue_${formIssue}`)}</span>
                </div>
              ) : null}
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('trigger')}</span>
                <input
                  className="text-input"
                  value={form.trigger}
                  placeholder={t('triggerPlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, trigger: event.target.value }))}
                />
                <span className={pluginStyles.fieldHint}>{t('triggerHint', { trigger: form.trigger.trim() || 'hours' })}</span>
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('displayName')}</span>
                <input
                  className="text-input"
                  value={form.displayName}
                  placeholder={t('displayNamePlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
                />
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('description')}</span>
                <input
                  className="text-input"
                  value={form.description}
                  placeholder={t('descriptionPlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                />
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('skill')}</span>
                <select
                  className={`text-input ${extraStyles.select}`}
                  value={form.skillId}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, skillId: event.target.value }))}
                >
                  <option value="">{t('skillNone')}</option>
                  {skills.map((skill) => (
                    <option key={skill.skillId} value={skill.skillId}>{skill.name}</option>
                  ))}
                </select>
              </label>
              <label className={pluginStyles.field}>
                <span className={pluginStyles.fieldLabel}>{t('extraInstruction')}</span>
                <textarea
                  className={`text-input ${extraStyles.textarea}`}
                  value={form.extraInstruction}
                  placeholder={t('extraInstructionPlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, extraInstruction: event.target.value }))}
                />
              </label>
              <label className={pluginStyles.field}>
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, isPublished: event.target.checked }))}
                />
                <span>
                  <strong>{t('publish')}</strong>
                  <span className={pluginStyles.fieldHint}>{t('publishHelp')}</span>
                </span>
              </label>
              <ToolUseCaseAssignment
                selectedUseCaseIds={form.assignedUseCaseIds}
                isPublished={form.isPublished}
                disabled={!canManage || saving}
                onChange={(assignedUseCaseIds) => setForm((current) => ({ ...current, assignedUseCaseIds }))}
              />
            </div>
            {canManage ? (
              <div className={pluginStyles.drawerFoot}>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void handleSave()}>
                  {saving ? t('saving') : t('save')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CommandsPage;
